import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';

// IndexedDB helpers for storing large files (like videos) in the draft
const DB_NAME = 'PostDraftDB';
const STORE_NAME = 'files';
const FILE_KEY = 'current_local_file';

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const saveFileToDB = async (file: File | null) => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    if (file) {
      tx.objectStore(STORE_NAME).put(file, FILE_KEY);
    } else {
      tx.objectStore(STORE_NAME).delete(FILE_KEY);
    }
    return new Promise((resolve) => {
      tx.oncomplete = () => resolve(true);
    });
  } catch (err) {
    console.error('Failed to save file to IndexedDB', err);
  }
};

const getFileFromDB = async (): Promise<File | null> => {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(FILE_KEY);
    return new Promise((resolve) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => resolve(null);
    });
  } catch (err) {
    console.error('Failed to get file from IndexedDB', err);
    return null;
  }
};

interface PostData {
  id?: string;
  mediaPath?: string;
  mediaPreview?: string;
  thumbnailPreview?: string;
  textEn?: string;
  textEs?: string;
  translations?: Record<string, { text: string, tiktokTitle?: string }>;
  initialTextEn?: string;
  initialTextEs?: string;
  trendId?: string | null;
  tags?: string[];
  campaignTags?: string[];
  evergreenTags?: string[];
  scheduledAt?: string;
  status?: string;
  platforms?: string[];
  gameName?: string;
  type?: string;
  title?: string;
  metadata?: any;
  media?: Array<{
    id: string;
    filePath: string;
    thumbnailPath?: string | null;
    mediaType: string;
    order: number;
    statusAiDiscovery?: string;
    aiMetadata?: any;
  }>;
  localFile?: File | null;
  videoDuration?: number | null;
  cropData?: any;
  crop?: { x: number; y: number } | null;
  zoom?: number;
  videoStartTime?: number | null;
  videoEndTime?: number | null;
  captions?: any[];
  config?: any;
  inactiveTags?: string[];
  tiktokMetadata?: {
    title: string;
    privacyLevel: string;
    allowComments: boolean;
    allowDuet: boolean;
    allowStitch: boolean;
    contentDisclosure: boolean;
    yourBrand: boolean;
    brandedContent: boolean;
  };
  youtubeMetadata?: {
    videoType: 'video' | 'short';
  };
  tumblrMetadata?: {
    contentDisclosure: boolean;
    yourBrand: boolean;
    brandedContent: boolean;
  };
  schedules?: Array<{
    id: string;
    socialAccountId: string;
    socialAccount?: {
      id: string;
      platform: string;
      name: string;
      handle?: string;
      avatarUrl?: string;
      externalId?: string;
      refreshToken?: string;
    };
    status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'DELETED';
    isBanger: boolean;
    scheduledAt: string;
    externalPostId?: string;
    likes: number;
    comments: number;
    shares: number;
    views: number;
    bookmarks: number;
    lastStatsUpdate?: string;
    result?: string;
    snapshots?: Array<{
      id: string;
      likes: number;
      comments: number;
      shares: number;
      views: number;
      bookmarks: number;
      timestamp: string;
    }>;
    commentsList?: Array<{
      id: string;
      authorName: string;
      authorHandle: string;
      authorAvatar?: string;
      authorFollowers?: number;
      authorVerified?: boolean;
      text: string;
      isReplied: boolean;
      isLiked: boolean;
      createdAt: string;
    }>;
  }>;
}

interface PostModalContextType {
  isOpen: boolean;
  isReadMode: boolean;
  currentPost: PostData | null;
  setCurrentPost: (post: PostData | null) => void;
  postDraft: PostData | null;
  openPostModal: (post?: PostData, options?: { forceEdit?: boolean }) => Promise<void>;
  openPostById: (postId: string) => void;
  openMediaById: (mediaId: string) => void;
  closePostModal: () => void;
  updateDraft: (draft: PostData | null) => void;
}

const PostModalContext = createContext<PostModalContextType | undefined>(undefined);

export function PostModalProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isReadMode, setIsReadMode] = useState(false);
  const [currentPost, setCurrentPostState] = useState<PostData | null>(null);
  const [postDraft, setPostDraft] = useState<PostData | null>(null);

  const setCurrentPost = (post: PostData | null) => {
    setCurrentPostState(post);
  };

  // Clear any stale draft data on mount
  useEffect(() => {
    localStorage.removeItem('post_draft');
    saveFileToDB(null);
  }, []);

  const updateDraft = (draft: PostData | null) => {
    setPostDraft(draft);
  };

  const openPostModal = async (post?: PostData, options?: { forceEdit?: boolean }) => {
    if (post) {
      setCurrentPost(post);
      setIsReadMode(options?.forceEdit ? false : true);
    } else {
      // New Post: always start fresh
      setCurrentPost(null);
      setIsReadMode(false);
    }
    setIsOpen(true);
  };

  const openPostById = async (postId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/posts/${postId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCurrentPost(response.data);
      setIsReadMode(true);
      setIsOpen(true);
    } catch (err) {
      console.error('Error fetching post by ID', err);
    }
  };

  const openMediaById = async (mediaId: string) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/social/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCurrentPost(response.data);
      setIsReadMode(true);
      setIsOpen(true);
    } catch (err) {
      console.error('Error fetching media by ID', err);
    }
  };

  const closePostModal = () => {
    setIsOpen(false);
    setCurrentPost(null);
    setIsReadMode(false);
  };

  return (
    <PostModalContext.Provider value={{ 
      isOpen, 
      isReadMode, 
      currentPost, 
      setCurrentPost,
      postDraft,
      openPostModal, 
      openPostById, 
      openMediaById, 
      closePostModal,
      updateDraft
    }}>
      {children}
    </PostModalContext.Provider>
  );
}

export function usePostModal() {
  const context = useContext(PostModalContext);
  if (context === undefined) {
    throw new Error('usePostModal must be used within a PostModalProvider');
  }
  return context;
}

